import {
	extractFileIdFromUrl,
	isInstantStorageUrl,
} from "@/lib/instant-file-url";
import { db } from "@/lib/instantdb";

/**
 * Check if a string is an absolute URL.
 */
function isUrl(value: string): boolean {
	return value.startsWith("http://") || value.startsWith("https://");
}

function isLocalAssetPath(value: string): boolean {
	return value.startsWith("/");
}

/**
 * Hook to resolve file paths to fresh URLs from InstantDB storage.
 * InstantDB storage URLs are pre-signed and expire after 7 days.
 * By querying $files, we get fresh URLs that won't 404.
 *
 * Also handles legacy data where full URLs were stored instead of paths.
 * For legacy URLs, we extract the file ID and look it up to get a fresh URL.
 */
export function useFileUrl(pathOrUrl: string | null | undefined) {
	const isRemoteUrl = !!(pathOrUrl && isUrl(pathOrUrl));
	const isLocalAsset = !!(pathOrUrl && isLocalAssetPath(pathOrUrl));
	const isLegacyUrl = !!(pathOrUrl && isInstantStorageUrl(pathOrUrl));

	// For legacy URLs, extract the file ID to look up fresh URL
	const fileIdFromUrl = isLegacyUrl ? extractFileIdFromUrl(pathOrUrl) : null;

	// Build the query - either by path (new data) or by id (legacy URL)
	const query = isLegacyUrl
		? fileIdFromUrl
			? { $files: { $: { where: { id: fileIdFromUrl } } } }
			: null
		: pathOrUrl
			? isLocalAsset
				? null
				: { $files: { $: { where: { path: pathOrUrl } } } }
			: null;

	const { data, isLoading } = db.useQuery(query);

	const file = data?.$files?.[0];

	return {
		url:
			(isRemoteUrl && !isLegacyUrl) || isLocalAsset
				? pathOrUrl
				: (file?.url ?? null),
		isLoading,
		isLegacy: isLegacyUrl,
	};
}

/**
 * Hook to resolve multiple file paths to fresh URLs.
 * Also handles legacy URLs by extracting file IDs and looking them up.
 */
export function useFileUrls(pathsOrUrls: string[] | null | undefined) {
	const validValues = pathsOrUrls?.filter(Boolean) ?? [];

	// Separate paths from legacy URLs and extract file IDs from URLs
	const pathsToQuery: string[] = [];
	const idsToQuery: string[] = [];
	const valueToType = new Map<string, "path" | "id">();
	const passthroughUrlMap = new Map<string, string>();

	validValues.forEach((value) => {
		if (isUrl(value)) {
			if (isInstantStorageUrl(value)) {
				const fileId = extractFileIdFromUrl(value);
				if (fileId) {
					idsToQuery.push(fileId);
					valueToType.set(value, "id");
				}
			} else {
				passthroughUrlMap.set(value, value);
			}
		} else if (isLocalAssetPath(value)) {
			passthroughUrlMap.set(value, value);
		} else {
			pathsToQuery.push(value);
			valueToType.set(value, "path");
		}
	});

	// Query by paths
	const { data: pathData, isLoading: isLoadingPaths } = db.useQuery(
		pathsToQuery.length > 0
			? {
					$files: {
						$: {
							where: {
								path: { $in: pathsToQuery },
							},
						},
					},
				}
			: null,
	);

	// Query by IDs (for legacy URLs)
	const { data: idData, isLoading: isLoadingIds } = db.useQuery(
		idsToQuery.length > 0
			? {
					$files: {
						$: {
							where: {
								id: { $in: idsToQuery },
							},
						},
					},
				}
			: null,
	);

	// Create lookup maps
	const pathUrlMap = new Map<string, string>();
	const idUrlMap = new Map<string, string>();

	if (pathData?.$files) {
		for (const file of pathData.$files) {
			pathUrlMap.set(file.path, file.url);
		}
	}

	if (idData?.$files) {
		for (const file of idData.$files) {
			idUrlMap.set(file.id, file.url);
		}
	}

	// Return URLs in the same order as input
	const urls = validValues.map((value) => {
		const type = valueToType.get(value);
		if (type === "id") {
			const fileId = extractFileIdFromUrl(value);
			return fileId ? (idUrlMap.get(fileId) ?? null) : null;
		}
		if (passthroughUrlMap.has(value)) {
			return passthroughUrlMap.get(value) ?? null;
		}
		return pathUrlMap.get(value) ?? null;
	});

	// Create combined map (original value -> url)
	const urlMap = new Map<string, string>();
	validValues.forEach((value, index) => {
		const url = urls[index];
		if (url) {
			urlMap.set(value, url);
		}
	});

	return {
		urls,
		urlMap,
		isLoading: isLoadingPaths || isLoadingIds,
	};
}
